import { useMemo, type FC, type ReactNode } from 'react';

import { GuideAnalyticsContext, NOOP_GUIDE_ANALYTICS, type IGuideAnalytics } from 'shared/hooks/useGuideAnalytics';
import { useGuideTracking } from 'shared/hooks/useGuideTracking';
import { useHostTracking } from 'shared/hooks/useHostTracking';
import { useApplicationContext } from 'shared/utils/applicationContext';

interface IGuideTrackingProviderProps {
  children: ReactNode;
}

const SobTrackingProvider: FC<IGuideTrackingProviderProps> = ({ children }) => {
  const tracking = useGuideTracking();
  const value = useMemo<IGuideAnalytics>(
    () => ({
      ...NOOP_GUIDE_ANALYTICS,
      trackPageCategory: tracking.trackPageCategory,
      trackArticleClick: tracking.trackArticleClick,
      trackFaqLinkClick: tracking.trackFaqLinkClick,
      trackFaqQuestionOpen: tracking.trackFaqQuestionOpen,
    }),
    [tracking.trackArticleClick, tracking.trackFaqLinkClick, tracking.trackFaqQuestionOpen, tracking.trackPageCategory],
  );

  return <GuideAnalyticsContext.Provider value={value}>{children}</GuideAnalyticsContext.Provider>;
};

const HostTrackingProvider: FC<IGuideTrackingProviderProps> = ({ children }) => {
  const { trackPageCategory, trackArticleClick, trackQuestion, trackPlaceAdClick, trackHowItsWork, trackSocialClick } =
    useHostTracking();
  const value = useMemo<IGuideAnalytics>(
    () => ({
      ...NOOP_GUIDE_ANALYTICS,
      trackPageCategory: () => trackPageCategory(),
      trackArticleClick,
      trackFaqQuestionOpen: (_questionId: number, question: string) => trackQuestion(question),
      trackPlaceAdClick,
      trackHowItsWorkClick: trackHowItsWork,
      trackSocialClick,
    }),
    [trackArticleClick, trackHowItsWork, trackPageCategory, trackPlaceAdClick, trackQuestion, trackSocialClick],
  );

  return <GuideAnalyticsContext.Provider value={value}>{children}</GuideAnalyticsContext.Provider>;
};

export const GuideTrackingProvider: FC<IGuideTrackingProviderProps> = ({ children }) => {
  const {
    custom: { pageType },
  } = useApplicationContext();

  if (pageType === 'host') {
    return <HostTrackingProvider>{children}</HostTrackingProvider>;
  }

  if (pageType === 'sob') {
    return <SobTrackingProvider>{children}</SobTrackingProvider>;
  }

  return <GuideAnalyticsContext.Provider value={NOOP_GUIDE_ANALYTICS}>{children}</GuideAnalyticsContext.Provider>;
};
